// ===================================================================
// KAIXO - ConversionInsights
// ===================================================================
// Panel dentro de Impulsa que muestra qué patrones ha detectado el
// algoritmo del feed a partir de las ventas cerradas en tu CRM. Cold
// start se comunica claramente con un contador "X/3 cerradas".
// ===================================================================

import React, { useState, useEffect } from 'react';
import { crmGetConversionInsights } from '../../services/crmApi';
import { getInterestLabel } from '../../constants/interests';
import ct from './i18n';
import './ConversionInsights.css';

function percent(v) {
    return `${Math.round(v * 100)}%`;
}

function BarRow({ label, freq }) {
    return (
        <div className="ci-bar-row">
            <span className="ci-bar-label">{label}</span>
            <div className="ci-bar-track">
                <div className="ci-bar-fill" style={{ width: percent(freq) }} />
            </div>
            <span className="ci-bar-pct">{percent(freq)}</span>
        </div>
    );
}

export default function ConversionInsights() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const lang = (() => {
        try { return JSON.parse(localStorage.getItem('kaixo_user') || '{}').language || 'español'; }
        catch { return 'español'; }
    })();

    useEffect(() => {
        let cancelled = false;
        crmGetConversionInsights()
            .then(({ data }) => { if (!cancelled) setData(data?.insights || null); })
            .catch(err => { if (!cancelled) setError(err.response?.data?.error || err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return <div className="ci-card ci-loading">{ct.loadingInsights || 'Analizando tus ventas...'}</div>;
    if (error) return <div className="ci-card ci-error">{error}</div>;
    if (!data) return null;

    // Cold start — aún no hay datos suficientes
    if (!data.ready) {
        const pct = Math.round((data.total / data.minimum) * 100);
        return (
            <div className="ci-card ci-cold">
                <div className="ci-title">🧠 {ct.ciTitle || 'Aprendizaje del algoritmo'}</div>
                <p className="ci-cold-text">
                    {data.reason}
                </p>
                <div className="ci-progress">
                    <div className="ci-progress-fill" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="ci-progress-label">{data.total} / {data.minimum}</div>
            </div>
        );
    }

    return (
        <div className="ci-card">
            <div className="ci-title">🧠 {ct.ciTitle || 'Aprendizaje del algoritmo'}</div>
            <p className="ci-sub">
                {ct.ciSub || 'Basado en tus últimas ventas cerradas, estos son los patrones del comprador que KAIXO está usando para priorizar tus latidos.'}
                {' '}<strong>{data.total} {ct.ciSales || 'ventas'}</strong>.
            </p>

            {data.topCountries?.length > 0 && (
                <div className="ci-section">
                    <div className="ci-section-title">{ct.ciCountries || 'Países que más compran'}</div>
                    {data.topCountries.map(r => (
                        <BarRow key={r.key} label={r.key} freq={r.freq} />
                    ))}
                </div>
            )}

            {data.topLanguages?.length > 0 && (
                <div className="ci-section">
                    <div className="ci-section-title">{ct.ciLanguages || 'Idiomas'}</div>
                    {data.topLanguages.map(r => (
                        <BarRow key={r.key} label={r.key} freq={r.freq} />
                    ))}
                </div>
            )}

            {data.topInterests?.length > 0 && (
                <div className="ci-section">
                    <div className="ci-section-title">{ct.ciInterests || 'Intereses'}</div>
                    {data.topInterests.map(r => (
                        <BarRow key={r.key} label={getInterestLabel(r.key, lang)} freq={r.freq} />
                    ))}
                </div>
            )}

            <div className="ci-footer">
                {ct.ciFooter || 'El algoritmo usa estos patrones para priorizar tus latidos ante usuarios similares.'}
            </div>
        </div>
    );
}
